import { BaseDataService } from './BaseDataService';
import { SurveyInstance, SurveyAssessmentViewModel, DemographicResponses, AssessmentTimerData } from '../../models';
import { NotificationService } from '../../services';
import { OpenBlobFile } from '../../utilities';

export class SurveyInstanceDataService extends BaseDataService<SurveyInstance> {
    constructor(notificationService: NotificationService) {
        // pondera wants these to show as Survey Invites rather than Survey Instances
        super('Survey Invite', 'Survey Invites', notificationService, 'surveyinstance');
    }

    public getSurveyVersionInfo(uniqueId: string) {
        const endpoint = `${this.endpointRoot}/${uniqueId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init)
            .then((response) => {
                return response.json();
            })
            .catch(() => {
                this.notificationService.addError('Error getting survey invite version information');
            });
    }

    public getAllPagedByOrganization(orgId: number, page: number, pageSize: number, filter?: string, sortProperty?: string, sortDirection?: string) {
        let endpoint = `${this.endpointRoot}/paged/byOrganization/${orgId}?page=${page}&pageSize=${pageSize}`;
        const init = this.buildRequestInit('GET');

        if (filter !== undefined) {
            endpoint = `${endpoint}&filter=${filter}`;
        }

        if (sortProperty !== undefined) {
            endpoint = `${endpoint}&sortProperty=${sortProperty}`;

            if (sortDirection !== undefined) {
                endpoint = `${endpoint}&sortDirection=${sortDirection}`;
            } else {
                endpoint = `${endpoint}&sortDirection=asc`;
            }
        }

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving ${this.entityTypePlural.toLowerCase()}.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    // this should only be used to get an assessment for a user to take
    getByUnique(uniqueId: string) {
        const endpoint = `${this.endpointRoot}/byunique/${uniqueId}`;
        const init: RequestInit = {
            method: 'GET',
            mode: 'cors',
            cache: 'default'
        };

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            throw {
                code: response.status,
                entityType: 'assessment'
            };
        });
    }

    setActive(id: number) {
        const endpoint = `${this.endpointRoot}/setActive/${id}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.json();
            }

            const message = `Error setting active state.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    getAnswers(uniqueId: string) {
        const endpoint = `${this.endpointRoot}/answers/${uniqueId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving survey responses.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    getCSV(uniqueId: string, downloadName: string) {
        const endpoint = `${this.endpointRoot}/csv/${uniqueId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving survey responses.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, downloadName + '.csv');
        });
    }

    getPDF(uniqueId: string, downloadName: string) {
        const endpoint = `${this.endpointRoot}/pdf/${uniqueId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving survey responses.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, downloadName + '.pdf');
        });
    }

    score(uniqueId: string) {
        const endpoint = `${this.endpointRoot}/score/${uniqueId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving survey results.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    submit(survey: SurveyAssessmentViewModel, demographicResponses: DemographicResponses, timerData: AssessmentTimerData) {
        const endpoint = `${this.endpointRoot}/submit/${survey.surveyInstanceUniqueID}`;

        var responses: any[] = [];

        survey.questions.map(question => {

            if ( question.questionType === 'text' || question.questionType === 'freeText' ) {

                responses.push({
                    questionId: question.id,
                    value: question.value
                });
            } else {

                var selectedOptionId;
                if (question.questionOptions) {
                    question.questionOptions.map(opt => {
                        if (opt.checked) {
                            selectedOptionId = opt.id;
                        }
                    });

                    responses.push({
                        questionId: question.id,
                        optionId: selectedOptionId
                    });
                }
            }
        });

        let init: RequestInit = {
            method: 'POST',
            mode: 'cors',
            cache: 'default',
            headers: new Headers({
                'content-type': 'application/json'
            }),
            body: JSON.stringify({
                responses: responses,
                demographicResponses: demographicResponses,
                timerData: timerData
            })
        };

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return;
            }
        });
    }

    updateSurveyVersion(surveyInstanceId: number, surveyVersionId: number) {
        const endpoint = `${this.endpointRoot}/${surveyInstanceId}/updateSurveyVersion/${surveyVersionId}`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init)
            .then((response) => {
                return response.json();
            })
            .catch(() => {
                this.notificationService.addError('Error updating survey version.');
            });
    }

    getAllForUser(userId: number) {
        const endpoint = `${this.endpointRoot}/getInvitesForUser/${userId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init)
            .then((response) => {
                return response.json();
            })
            .catch(() => {
                this.notificationService.addError('Error getting invites for user.');
            });
    }

    public list(surveyId: number, userId: number) {
        const endpoint = `${this.endpointRoot}/list/${surveyId}/${userId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init)
            .then((response) => {
                return response.json();
            })
            .catch(() => {
                this.notificationService.addError('Error getting survey invite list information');
            });
    }
}


// WEBPACK FOOTER //
// ./src/services/api/SurveyInstanceData.tsx